import { useTranslation } from 'react-i18next';

import { useKeyboardLayout } from 'app/modules/wallforming/components/input-binding/hooks/useGetInputActionLabel';
import {
    useGetInputBindingSlot,
    type BindingEditorProps
} from 'app/modules/wallforming/components/input-binding/hooks/useGetInputBindingSlot';
import {
    CAPTURE_TOGGLE_ATTRIBUTE,
    CAPTURE_ZONE_ATTRIBUTE
} from 'app/modules/wallforming/components/input-binding/hooks/useInputCaptureHandler';
import { SlotActionIcon } from 'app/modules/wallforming/components/input-binding/SlotActionIcon';
import { formatChordLabel } from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

const toggleAttribute = { [CAPTURE_TOGGLE_ATTRIBUTE]: true } as const;

/** Records keys and mouse clicks via capture mode. */
export const ChordCaptureEditor = ({ actionId, slot }: BindingEditorProps) => {
    const { t } = useTranslation();

    const keyboardLayout = useKeyboardLayout();
    const { chord, isModified, isReadOnly, isCapturing } = useGetInputBindingSlot(actionId, slot);

    const { startInputCapture, cancelInputCapture, assignInputBinding, resetInputBindingSlot } =
        usePlacementStore.getState();

    const toggleCapture = () => (isCapturing ? cancelInputCapture() : startInputCapture(actionId, slot));
    const chipState = isCapturing
        ? 'border-blue-500 bg-blue-50 font-medium text-blue-700 shadow-xs'
        : chord
          ? 'border-border-ultra-light text-text-primary hover:border-border-strong'
          : 'border-dashed border-border-ultra-light text-text-placeholder hover:border-border-strong';

    return (
        <div className="flex min-w-0 items-center">
            <div
                className={`flex h-7 w-28 shrink-0 cursor-pointer items-center justify-center rounded border bg-background-default px-1 text-xs select-none transition-colors ${chipState}`}
                role="button"
                tabIndex={0}
                title={chord ? undefined : t('labelKeyBindingUnassignedTooltip')}
                {...{ [isCapturing ? CAPTURE_ZONE_ATTRIBUTE : CAPTURE_TOGGLE_ATTRIBUTE]: true }}
                onClick={toggleCapture}
            >
                <span className="truncate">
                    {isCapturing
                        ? t('labelKeyBindingPressKeys')
                        : chord
                          ? formatChordLabel(chord, keyboardLayout)
                          : t('labelKeyBindingNone')}
                </span>
            </div>
            <SlotActionIcon
                active={isCapturing}
                attributes={toggleAttribute}
                disabled={isReadOnly}
                icon="edit"
                labelKey="labelKeyBindingEditSlot"
                onClick={toggleCapture}
            />
            <SlotActionIcon
                disabled={isReadOnly || !chord}
                icon="close"
                labelKey="labelKeyBindingClearSlot"
                onClick={() => assignInputBinding(actionId, slot, null)}
            />
            <SlotActionIcon
                disabled={isReadOnly || !isModified}
                icon="restart_alt"
                labelKey="labelKeyBindingResetSlot"
                onClick={() => resetInputBindingSlot(actionId, slot)}
            />
        </div>
    );
};
